import { cn } from '@heroui/react'
import { MIN_USER_STORAGE_QUOTA_GB } from '@qse/constants'
import { formatStorageValue } from '@qse/utils'
import { useRef, useState } from 'react'

import Input from './input'

const BYTES_IN_MB = 1024 ** 2
const BYTES_IN_GB = 1024 ** 3

const MIN_MB = Math.round(MIN_USER_STORAGE_QUOTA_GB * 1024) // 10
const MIN_GB = 1

type Unit = 'MB' | 'GB'
const UNITS: Unit[] = ['MB', 'GB']

export interface StorageQuotaInputProps {
  name?: string
  label?: string
  isRequired?: boolean
  /**
   * Prefill value in BYTES (e.g. the user's existing quota when editing).
   * Passing bytes avoids the double-rounding that happens when converting
   * through a coarsely-rounded GB value (e.g. 500 MB would round-trip to 502).
   */
  defaultValueBytes?: number
  /** Upper bound the user may allocate, in bytes. */
  availableBytes: number
  /** Allow empty submission (used for edit mode when user had no quota set). */
  allowEmpty?: boolean
}

const toBytes = (value: number, unit: Unit): number =>
  unit === 'MB' ? value * BYTES_IN_MB : value * BYTES_IN_GB

const toGB = (value: number, unit: Unit): number => (unit === 'MB' ? value / 1024 : value)

const pickInitial = (defaultValueBytes: number | undefined): { unit: Unit; value: string } => {
  if (!defaultValueBytes || defaultValueBytes <= 0) {
    return { unit: 'GB', value: '' }
  }

  // Sub-GB values display in MB as whole numbers; ≥ 1 GB displays in GB
  // rounded to 2 decimals. Conversion happens exactly once from bytes.
  if (defaultValueBytes < BYTES_IN_GB) {
    return { unit: 'MB', value: String(Math.round(defaultValueBytes / BYTES_IN_MB)) }
  }

  const gb = defaultValueBytes / BYTES_IN_GB

  return { unit: 'GB', value: String(Number(gb.toFixed(2))) }
}

const StorageQuotaInput = ({
  name = 'storageQuota',
  label = 'Storage Quota',
  isRequired = true,
  defaultValueBytes,
  availableBytes,
  allowEmpty = false,
}: StorageQuotaInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const initial = pickInitial(defaultValueBytes)
  const [unit, setUnit] = useState<Unit>(initial.unit)
  const [value, setValue] = useState<string>(initial.value)

  const isGB = unit === 'GB'
  const hasInput = value !== '' && !Number.isNaN(Number(value))
  const numVal = hasInput ? (isGB ? parseFloat(value) : parseInt(value, 10)) : NaN
  const valueGB = hasInput ? toGB(numVal, unit) : 0

  const helperText = isGB ? 'Min 1 GB' : 'Min 10 MB'

  const handleChange = (raw: string) => {
    if (isGB) {
      if (raw === '' || /^\d*\.?\d*$/.test(raw)) setValue(raw)
    } else {
      if (raw === '' || /^\d+$/.test(raw)) setValue(raw)
    }
  }

  const handleUnitChange = (u: Unit) => {
    if (u === unit) return
    setUnit(u)
    setValue('')
    queueMicrotask(() => inputRef.current?.focus())
  }

  // Self-contained validator — closes over `unit` so it reflects the
  // currently selected mode. HeroUI derives isInvalid + errorMessage from
  // whatever this returns; we deliberately do NOT pass isInvalid ourselves,
  // otherwise it would force a controlled valid state and suppress the
  // "required" error on submit (matches how API Quota works).
  const validate = (raw: string) => {
    if (!raw) {
      return isRequired && !allowEmpty ? 'Storage Quota is required' : null
    }
    const num = isGB ? parseFloat(raw) : parseInt(raw, 10)

    if (Number.isNaN(num)) {
      return 'Enter a valid number'
    }
    const minForUnit = isGB ? MIN_GB : MIN_MB

    if (num < minForUnit) {
      return isGB ? 'Minimum storage is 1 GB' : 'Minimum storage is 10 MB'
    }
    if (toBytes(num, unit) > availableBytes) {
      return `Exceeds available quota (${formatStorageValue(availableBytes)})`
    }

    return null
  }

  const endContent = (
    <div className="flex h-full items-center gap-2">
      <div aria-hidden="true" className="h-5 w-px shrink-0 bg-gray-200 dark:bg-default-600" />
      <div aria-label="Storage unit" className="flex shrink-0 items-center gap-0.5" role="group">
        {UNITS.map(u => (
          <button
            key={u}
            aria-pressed={unit === u}
            type="button"
            className={cn(
              'rounded-md px-2.5 py-1 text-xs font-medium transition-colors cursor-pointer',
              unit === u
                ? 'bg-primary text-white'
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white',
            )}
            onClick={() => handleUnitChange(u)}
          >
            {u}
          </button>
        ))}
      </div>
    </div>
  )

  const description = (
    <span className="text-gray-400 dark:text-gray-500">
      {helperText} · Available: {formatStorageValue(availableBytes)} (remaining)
    </span>
  )

  return (
    <>
      <Input
        ref={inputRef}
        description={description}
        endContent={endContent}
        inputMode={isGB ? 'decimal' : 'numeric'}
        isRequired={isRequired}
        label={label}
        labelPlacement="outside"
        placeholder={isGB ? 'e.g. 1.5' : 'e.g. 500'}
        validate={validate}
        value={value}
        onChange={handleChange}
      />
      <input name={name} type="hidden" value={hasInput ? String(valueGB) : ''} />
    </>
  )
}

export default StorageQuotaInput
